// homeCommunity/container.js
import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { loadStripe } from '@stripe/stripe-js';
import { useSnackbar } from 'notistack';

// Import view component
import HomeCommunityView from './view';

// Import API functions
import { getMessageBoard, inviteCommunityMembers, joinBooking } from 'api';
import { REACT_APP_STRIPE_KEY } from 'config/api';

// Initialize Stripe
const stripePromise = REACT_APP_STRIPE_KEY ? loadStripe(REACT_APP_STRIPE_KEY) : null;

// Extend dayjs with plugins
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

const HomeCommunityContainer = ({
    slug,
    community,
    members = [],
    upcomingEvents = [],
    communityBookings = [],
    venues = [],
    isAdmin = false,
    my_membership = null,
    onJoinRequest = () => { },
    recentMessages = [],
    onEventsUpdate = () => { }, // Add onEventsUpdate callback
    onMembershipUpdate = () => { },
    isAuthenticated = false,
    onShowLogin = () => { },
}) => {
    const { enqueueSnackbar } = useSnackbar();
    
    // All your state management logic here
    const [tabValue, setTabValue] = useState(0);
    const [messages, setMessages] = useState([]);
    const [selectedMessageId, setSelectedMessageId] = useState(null); // NEW: For message selection
    const [stats, setStats] = useState({
        totalMembers: members.length || 0,
        activeToday: 0,
        totalMessages: 0,
        totalEvents: upcomingEvents.length || 0,
    });
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [activeChatMember, setActiveChatMember] = useState(null);
    const [selectedMemberDetail, setSelectedMemberDetail] = useState(null);
    
    // Join booking state
    const [joinBookingDialogOpen, setJoinBookingDialogOpen] = useState(false);
    const [selectedBookingToJoin, setSelectedBookingToJoin] = useState(null);
    const [joinBookingLoading, setJoinBookingLoading] = useState(false);


    // Generate dummy data (move to separate utils if needed)
    const [activeMembers, setActiveMembers] = useState([
        { id: 1, name: 'Pedro Sanchez', avatar: null, location: 'Floor 3, 6-8 Bonhill St, Shoreditch', lastSeen: dayjs().subtract(10, 'minute').format(), joined: dayjs().subtract(2, 'day').format() },
        { id: 2, name: 'Oliver Thompson', avatar: null, location: 'Remote', lastSeen: dayjs().subtract(25, 'minute').format(), joined: dayjs().subtract(5, 'day').format() },
        { id: 3, name: 'Ferlucio Gomez', avatar: null, location: 'Floor 3, 6-8 Bonhill St, Shoreditch', lastSeen: dayjs().subtract(1, 'hour').format(), joined: dayjs().subtract(7, 'day').format() },
        { id: 4, name: 'Ana Rodriguez', avatar: null, location: 'Remote', lastSeen: dayjs().subtract(3, 'hour').format(), joined: dayjs().subtract(12, 'day').format() },
    ]);

    const [events, setEvents] = useState(upcomingEvents || []);

    useEffect(() => {
        if (selectedMemberDetail && members) {
            // Find the updated member in the members array
            const updatedMember = members.find(m => m.id === selectedMemberDetail.id || m.slug === selectedMemberDetail.slug);
            if (updatedMember) {
                setSelectedMemberDetail(updatedMember);
            }
        }
    }, [members, selectedMemberDetail]);
    
    // Update events when upcomingEvents prop changes
    useEffect(() => {
        setEvents(upcomingEvents || []);
    }, [upcomingEvents]);

    // All your useEffect hooks and event handlers here
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setStats({
                    totalMembers: members.length || 7,
                    activeToday: activeMembers.length,
                    totalMessages: 0,
                    totalEvents: events.length,
                });
                setLoading(false);
            } catch (error) {
                console.error('Error fetching community data:', error);
                setLoading(false);
            }
        };
        fetchData();
    }, [slug, members.length, activeMembers.length, events.length]);

    useEffect(() => {
        if (recentMessages && recentMessages.length > 0) {
            setMessages(recentMessages);
        }
    }, [recentMessages]);

    // Event handlers
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleDateSelect = (date) => {
        setSelectedDate(date);
    };

    const handleOpenChat = (member, action = 'profile') => {
        setActiveChatMember(member);
    };

    const handleCloseChat = () => {
        setActiveChatMember(null);
    };

    const handleOpenMemberDetail = (member, action = 'profile') => {
        setSelectedMemberDetail(member);
    };

    const handleCloseMemberDetail = () => {
        setSelectedMemberDetail(null);
    };

    const handleToggleSidebarCollapse = () => {
        setSidebarCollapsed(!sidebarCollapsed);
    };

    const handleViewAllAnnouncements = () => {
        setTabValue(1);
    };

    const handleCreateAnnouncement = () => {
        setTabValue(1);
    };

    // NEW: Handle opening a specific message
    const handleOpenMessage = (messageId) => {
        setSelectedMessageId(messageId);
        setTabValue(1); // Navigate to Messages tab
    };

    const handleViewAllEvents = () => {
        setTabValue(2);
    };

    const handleCreateEvent = () => {
        setTabValue(2);
    };

    const handleViewAllMembers = () => {
        setTabValue(3);
    };

    const handleCreateMessage = (newMessage) => {
        const message = {
            ...newMessage,
            id: Date.now(),
            sender: { first_name: 'Admin', last_name: '' },
            planned_sent: dayjs().format(),
            featured: newMessage.featured ? 1 : 0,
        };
        setMessages(prev => [message, ...prev]);
    };

    const handleCreateEventAction = () => {
        // Just switch to events tab, don't create fake events
        setTabValue(2);
    };

    const handleInviteMember = async (inviteData) => {
        try {
            // inviteData should contain: { emails: string[], role?: string, personal_message?: string }
            const emails = Array.isArray(inviteData.emails) 
                ? inviteData.emails 
                : [inviteData.email || inviteData.emails];
            
            const response = await inviteCommunityMembers(slug, {
                emails: emails.filter(email => email && email.trim()),
                role: inviteData.role || 'PA', // Default to Participant
                personal_message: inviteData.personal_message || ''
            });
            
            if (response.data) {
                const result = response.data;
                
                // Notify about results
                if (result.invitations_sent > 0) {
                    console.log(`Successfully sent ${result.invitations_sent} invitation(s)`);
                }
                
                if (result.already_members?.length > 0) {
                    console.log(`Already members: ${result.already_members.join(', ')}`);
                }
                
                if (result.already_invited?.length > 0) {
                    console.log(`Already invited: ${result.already_invited.join(', ')}`);
                }
                
                if (result.invalid_emails?.length > 0) {
                    console.log(`Invalid emails: ${result.invalid_emails.join(', ')}`);
                }
                
                return result;
            }
        } catch (error) {
            console.error('Error inviting members:', error);
            throw error;
        }
    };

    const handleJoinRequest = (joinData) => {
    };

    // Join booking handlers
    const handleOpenJoinBookingDialog = (booking) => {
        if (!isAuthenticated) {
            onShowLogin();
            return;
        }
        setSelectedBookingToJoin(booking);
        setJoinBookingDialogOpen(true);
    };

    const handleCloseJoinBookingDialog = () => {
        setJoinBookingDialogOpen(false);
        setSelectedBookingToJoin(null);
    };

    const handleConfirmJoinBooking = async (booking) => {
        if (!booking.booking_plan_code) {
            enqueueSnackbar('This booking cannot be joined', { variant: 'error' });
            return;
        }

        setJoinBookingLoading(true);
        try {
            const response = await joinBooking({
                bookings: [{
                    code: booking.booking_plan_code,
                    price: parseFloat(booking.price || 0) + parseFloat(booking.vat_amount || 0),
                }]
            });

            // Response can be directly the data or wrapped in .data depending on axios config
            const sessionId = response.sessionId || response.data?.sessionId;
            if (sessionId) {
                // Redirect to Stripe checkout
                const stripe = await stripePromise;
                const { error } = await stripe.redirectToCheckout({
                    sessionId: sessionId,
                });
                
                if (error) {
                    enqueueSnackbar('Payment error: ' + error.message, { variant: 'error' });
                }
            } else {
                enqueueSnackbar('Failed to create payment session', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error joining booking:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
            enqueueSnackbar('Failed to join booking: ' + errorMsg, { variant: 'error' });
        } finally {
            setJoinBookingLoading(false);
        }
    };

    // Pass all props and handlers to view component
    return (
        <HomeCommunityView
            // Data props
            community={community}
            members={members}
            activeMembers={activeMembers}
            events={events}
            communityBookings={communityBookings}
            messages={messages}
            venues={venues}
            stats={stats}
            my_membership={my_membership}

            // State props
            tabValue={tabValue}
            loading={loading}
            selectedDate={selectedDate}
            sidebarOpen={sidebarOpen}
            sidebarCollapsed={sidebarCollapsed}
            activeChatMember={activeChatMember}
            selectedMemberDetail={selectedMemberDetail}
            isAdmin={isAdmin}
            selectedMessageId={selectedMessageId} // NEW: Pass selected message ID
            isAuthenticated={isAuthenticated}
            
            // Join booking state
            joinBookingDialogOpen={joinBookingDialogOpen}
            selectedBookingToJoin={selectedBookingToJoin}
            joinBookingLoading={joinBookingLoading}

            // Handler props
            onTabChange={handleTabChange}
            onDateSelect={handleDateSelect}
            onOpenChat={handleOpenChat}
            onCloseChat={handleCloseChat}
            onOpenMemberDetail={handleOpenMemberDetail}
            onCloseMemberDetail={handleCloseMemberDetail}
            onToggleSidebarCollapse={handleToggleSidebarCollapse}
            onViewAllAnnouncements={handleViewAllAnnouncements}
            onCreateAnnouncement={handleCreateAnnouncement}
            onOpenMessage={handleOpenMessage} // NEW: Pass message opening handler
            onViewAllEvents={handleViewAllEvents}
            onCreateEvent={handleCreateEvent}
            onViewAllMembers={handleViewAllMembers}
            onCreateMessage={handleCreateMessage}
            onCreateEventAction={handleCreateEventAction}
            onInviteMember={handleInviteMember}
            onJoinRequest={handleJoinRequest}
            onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
            onEventsUpdate={onEventsUpdate} // Pass through the events update callback
            onMembershipUpdate={onMembershipUpdate} // Pass through the membership update callback
            
            // Join booking handlers
            onJoinBooking={handleOpenJoinBookingDialog}
            onCloseJoinBookingDialog={handleCloseJoinBookingDialog}
            onConfirmJoinBooking={handleConfirmJoinBooking}
        />
    );
};

export default HomeCommunityContainer;